import React, { Component, ReactNode } from "react";
import ResizeObserverPollyfill from "resize-observer-polyfill";

export interface ResizeEntry extends ResizeObserverEntry {}

/** `ResizeObserver` requires a single DOM element child and will error otherwise. */
export interface ResizeObserverProps {
  children: ReactNode;
  /**
   * Callback invoked when the wrapped element resizes.
   *
   * The `entries` array contains an entry for each observed element. In the
   * default case (no `observeParents`), the array will contain only one
   * element: the single child of the `ResizeObserver`.
   *
   * Note that this method is called _asynchronously_ after a resize is
   * detected and typically it will be called no more than once per frame.
   */
  onResize: (entries: ResizeEntry[]) => void;

  /**
   * If `true`, all parent DOM elements of the container will also be
   * observed for size changes. The array of entries passed to `onResize`
   * will now contain an entry for each parent element up to the root of the
   * document.
   *
   * Only enable this prop if a parent element resizes in a way that does
   * not also cause the child element to resize.
   *
   * @default false
   */
  observeParents?: boolean;
}

/**
 * `ResizeObserver` is a component that provides a "resize" event for its single DOM element child.
 * It is a thin wrapper around ResizeObserver to provide React bindings.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 */
export class ResizeObserver extends Component<ResizeObserverProps> {
  static defaultProps: Partial<ResizeObserverProps> = {
    observeParents: false,
  };

  element: Element | null = null;
  observer = new ResizeObserverPollyfill(this.props.onResize);
  observerRef = React.createRef<HTMLDivElement>();

  render() {
    return <div ref={this.observerRef}>{React.Children.only(this.props.children)}</div>;
  }

  componentDidMount() {
    this.observeElement();
  }

  componentDidUpdate(prevProps: ResizeObserverProps) {
    this.observeElement(this.props.observeParents !== prevProps.observeParents);
  }

  componentWillUnmount() {
    this.observer.disconnect();
  }

  /**
   * Observe the DOM element, if defined and different from the currently
   * observed element. Pass `force` argument to skip element checks and always
   * re-observe.
   */
  observeElement(force = false) {
    const element = this.observerRef.current.firstElementChild;
    if (!(element instanceof Element)) {
      // stop everything if not defined
      this.observer.disconnect();
      return;
    }

    if (element === this.element && !force) {
      // quit if given same element -- nothing to update (unless forced)
      return;
    } else {
      // clear observer list if new element
      this.observer.disconnect();
      // remember element reference for next time
      this.element = element;
    }

    // observer callback is invoked immediately when observing new elements
    this.observer.observe(element);

    if (this.props.observeParents) {
      let parent = element.parentElement;
      while (parent != null) {
        this.observer.observe(parent);
        parent = parent.parentElement;
      }
    }
  }
}
